import { useCallback, useMemo } from "react";
import { ChartType } from "chart.js";
import { head, map, size, sumBy } from "lodash";
import { Item } from "@charty/legend/item/model";
import { Serie } from "@charty/models";
import { darken, transparentize } from "@glossy";
import { background, chart, content } from "@glossy/theme";
import { GlossyTheme } from "@glossy/theme/types";
import { getMetricLabel } from "@linkfluence/i18n";
import { AbstractDate, Range } from "@linkfluence/models/date";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { endOf, getDatesFromRange, startOf } from "@tootools/date";

export const useLegendSeries = (
  series: Serie[],
  locale: Locale,
  theme: GlossyTheme
) =>
  useMemo(
    () =>
      map(series, ({ id, label, data, type }) => {
        return {
          id,
          label: getMetricLabel(locale, label as Metric) || label,
          count: sumBy(data, "count"),
          color:
            type === "bar" ? content({ theme }, "secondary") : chart({ theme }),
        };
      }),
    [locale, series, theme]
  );

export const useTotal = (total: number, legendSeries: Item[]) =>
  useMemo(() => {
    if (total) {
      return total;
    } else {
      return sumBy(legendSeries, "count");
    }
  }, [legendSeries, total]);

export const getBarSerieConfig = (theme) => {
  const color = content({ theme }, "secondary");
  return {
    yAxisID: "y",
    order: 2,
    accentColor: color,
    backgroundColor: color,
    hoverBackgroundColor: darken(0.1, color),
  };
};

export const getLineSerieConfig = (theme) => ({
  yAxisID: "y2",
  order: 1,
  accentColor: chart({ theme }),
  backgroundColor: chart({ theme }),
  fill: false,
  tension: 0.1,
  pointBorderWidth: 3,
  pointHoverRadius: 6,
  pointHoverBorderWidth: 24,
  pointRadius: () => 3,
  pointHitRadius: 16,
  borderColor: chart({ theme }),
  pointBorderColor: chart({ theme }),
  pointBackgroundColor: background({ theme }),
  pointHoverBackgroundColor: chart({ theme }),
  pointHoverBorderColor: transparentize(0.8, chart({ theme })),
});

const getPointRadius = (context) => {
  const xAxis = context.chart.scales["x"];
  const tickWidth = xAxis.width / (xAxis.max - xAxis.min);
  return tickWidth > 40 ? 4 : 0;
};

export const updateOnHover = (barchart, hovered, theme) => {
  for (let i = 0; i < barchart.data.datasets.length; i++) {
    const dataset = barchart.data.datasets[i];
    if (hovered) {
      if (dataset.id === hovered) {
        dataset.borderColor = dataset.accentColor;
        dataset.backgroundColor = dataset.accentColor;
      } else {
        dataset.borderColor = dataset.accentFadedColor;
        dataset.pointRadius = () => 0;
        dataset.pointBorderWidth = 0;
        dataset.pointBorderColor = dataset.accentFadedColor;
        dataset.pointBackgroundColor = dataset.accentFadedColor;
        dataset.backgroundColor = dataset.accentFadedColor;
      }
    } else {
      dataset.backgroundColor = dataset.accentColor;
      dataset.borderColor = dataset.accentColor;
      dataset.pointBorderWidth = 3;
      dataset.pointBorderColor = dataset.accentColor;
      dataset.pointBackgroundColor = background({ theme });
      dataset.pointRadius = getPointRadius;
    }
  }
};

export const includeEndOfRange = (data, range) =>
  size(data) > size(getDatesFromRange(range));

export const useDatesFromRanges = (range: Range, withEndOfRange: boolean) =>
  useMemo(
    () => getDatesFromRange(range, withEndOfRange),
    [range, withEndOfRange]
  );

export const useFormatData = (
  series: Serie[],
  range: Range,
  theme: GlossyTheme
) => {
  const withEndOfRange = includeEndOfRange(head(series)?.data, range);
  const datesFromRanges = useDatesFromRanges(range, withEndOfRange);
  return useMemo(() => {
    return {
      labels: map(datesFromRanges, (date) => date.toISOString()),
      datasets: map(series, ({ id, data, label, type }) => {
        const serieConfig =
          type === "bar" ? getBarSerieConfig(theme) : getLineSerieConfig(theme);
        return {
          id,
          type: type as ChartType,
          label,
          data: map(data, "count"),
          accentFadedColor: content({ theme }, "tertiary"),
          spanGaps: true,
          ...serieConfig,
        };
      }),
    };
  }, [datesFromRanges, series, theme]);
};

export const useOnClickItem = (
  series: Serie[],
  range: Range,
  onClickItem: (
    uid: Item["id"],
    range: { from: AbstractDate; to: AbstractDate }
  ) => void
) => {
  const withEndOfRange = includeEndOfRange(head(series)?.data, range);
  const datesFromRanges = useDatesFromRanges(range, withEndOfRange);
  return useCallback(
    (datasetIndex: number, index: number) => {
      const date = datesFromRanges[index];
      onClickItem(series[datasetIndex].id, {
        from: startOf(date, range.interval).toISOString(),
        to: endOf(date, range.interval).toISOString(),
      });
    },
    [datesFromRanges, series, onClickItem, range.interval]
  );
};
